import React from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { BillsAddButton } from "@pages/BillsPage/components/BillsAddButton/BillsAddButton";
import { AddHouseModal } from "@features/AddHouseModal/AddHouseModal";
import { useChubbInsuranceModal } from "@features/ChubbInsuranceModal/useChubbInsuranceModal";
import { JointBillListFilters } from "@features/JointBillListFilters/JointBillListFilters";
import { Button } from "@components/Button";
import {
  HouseIcon,
  HousePlusSquareIcon,
  PersonPlusIcon,
  PlusIcon,
  ShieldIcon,
} from "@components/icons";
import { useAddBillModal } from "@hooks/useAddBillModal";
import { useAddPayerModal } from "@hooks/useAddPayerModal";
import { useCurrentJointBillServicePeriodId } from "@hooks/useCurrentJointBillServicePeriodId";
import { useFeatureConfig } from "@hooks/useFeatureFlags";
import { usePOAModal } from "@hooks/usePOAModal";
import { useModalStore } from "@store/modal/modal";
import {
  useJointBillServicePeriodDataQueries,
  useOverviewQuery,
} from "@services/overview/queries";
import { usePOASignedDocumentMetadataQuery } from "@services/powersOfAttorney/queries";

export const BillsPageActionBar: React.FC = () => {
  const { t } = useTranslation();
  const { create } = useModalStore();
  const { isPoaEnabled, isChubbInsuranceEnabled } = useFeatureConfig();

  const { data: POAdocumentMetadata } =
    usePOASignedDocumentMetadataQuery(isPoaEnabled);

  const { openAddPayerModal } = useAddPayerModal();

  const openAddHouseModal = () => {
    create((modalProps) => <AddHouseModal {...modalProps} />);
  };

  const { openPOAModal } = usePOAModal();
  const { openAddBillModal } = useAddBillModal();
  const { openChubbInsuranceModal } = useChubbInsuranceModal();

  const { data: overview, isLoading: isOverviewLoading } = useOverviewQuery();
  const currentPeriodId = useCurrentJointBillServicePeriodId();

  const jointBillPeriodQueries = useJointBillServicePeriodDataQueries(
    (overview?.jointBillServices ?? []).map((jbs) => ({
      jointBillServiceId: jbs.jointBillServiceId,
      periodId: currentPeriodId ?? 0,
    })),
  );

  const isChubbBillInsuranceButtonDisplayed =
    isChubbInsuranceEnabled &&
    !isOverviewLoading &&
    jointBillPeriodQueries.some(
      (query) =>
        query.data?.insuranceStatus === "active" ||
        query.data?.insuranceStatus === "inactive",
    );

  return (
    <div className="flex w-full justify-end gap-3 lg:justify-between">
      <div
        className={twMerge(
          "hidden sm:flex xl:hidden",
          isChubbBillInsuranceButtonDisplayed &&
            "sm:hidden md:flex lg:flex xl:flex 2xl:hidden",
        )}
      >
        <BillsAddButton />
      </div>
      <div
        className={twMerge(
          "hidden gap-3 xl:flex",
          isChubbBillInsuranceButtonDisplayed && "xl:hidden 2xl:flex",
        )}
      >
        <Button
          icon={PlusIcon}
          onClick={() => openAddBillModal({ shouldSuggestAutomaticPoa: true })}
          aria-label={t("billsPage.actions.addProvider")}
        >
          {t("billsPage.actions.addProvider")}
        </Button>
        <Button
          variant="secondary"
          icon={PersonPlusIcon}
          onClick={openAddPayerModal}
          aria-label={t("billsPage.actions.addPayer")}
        >
          {t("billsPage.actions.addPayer")}
        </Button>
        <Button
          variant="secondary"
          icon={HouseIcon}
          onClick={openAddHouseModal}
          aria-label={t("billsPage.actions.addHouse")}
        >
          {t("billsPage.actions.addHouse")}
        </Button>
        {POAdocumentMetadata?.succeeded && (
          <Button
            variant="secondary"
            icon={HousePlusSquareIcon}
            onClick={() => openPOAModal({ showExistingConsent: true })}
            aria-label={t("billsPage.actions.powerOfAttorney")}
          >
            {t("billsPage.actions.powerOfAttorney")}
          </Button>
        )}
        {isChubbBillInsuranceButtonDisplayed && (
          <Button
            variant="secondary"
            icon={ShieldIcon}
            onClick={openChubbInsuranceModal}
            aria-label={t("billsPage.actions.chubbInsurance")}
          >
            {t("billsPage.actions.chubbInsurance")}
          </Button>
        )}
      </div>
      <div className="lg:ml-auto">
        <JointBillListFilters path="/bills" />
      </div>
    </div>
  );
};
